package org.matthewo.pages.admin

import csstype.PropertiesBuilder
import emotion.react.css
import io.ktor.client.call.*
import io.ktor.client.plugins.resources.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.coroutines.launch
import org.matthewo.client
import org.matthewo.components.Dialog
import org.matthewo.components.DialogField
import org.matthewo.hooks.useCoroutineScope
import org.matthewo.routes.API
import org.matthewo.table.Column
import org.matthewo.table.Order
import org.matthewo.table.Table
import org.matthewo.useLoading
import org.matthewo.useTitleHeader
import react.FC
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.useMemo
import react.useState
import web.cssom.*
import web.html.ButtonType
import web.html.button
import web.prompts.alert
import web.prompts.confirm

private fun PropertiesBuilder.actionButton(color: Color) {
    backgroundColor = color
    userSelect = None.none
    border = None.none
    borderRadius = 0.5.em
    padding = Padding(0.25.em, 0.75.em)
    cursor = Cursor.pointer
}

private data class UsersTableConfig(val onRemove: (String) -> Unit)

private val UsersTableView = Table<String, UsersTableConfig>(
    pinHeader = true, pinFirstColumn = false, sortQuery = false, initialSortColumn = 0,
    Column("Username", { it }, compareBy { it.lowercase() }, Order.ASCENDING),
    Column("", { "" }, null, Order.ASCENDING) { (onRemove), user, _ ->
        css {
            textAlign = TextAlign.right
            width = 6.em
        }
        button {
            css {
                actionButton(NamedColor.red)
            }
            type = ButtonType.button
            onClick = {
                it.stopPropagation()
                onRemove(user)
            }
            +"Remove"
        }
    },
)

val UsersTable = FC("UsersTable") {
    useTitleHeader { "Users" }

    val coroutineScope = useCoroutineScope()

    var users by useState<List<String>>()
    suspend fun loadUsers() {
        users = client.get(API.Admin.Users()).body<List<String>>()
    }

    useMemo {
        coroutineScope.launch {
            loadUsers()
        }
    }

    var enterNewUser by useState(false)

    useLoading(null == users)

    div {
        css {
            display = Display.flex
            flexDirection = FlexDirection.column
            gap = 1.em
            minWidth = 20.em
            maxWidth = 100.pct
            marginTop = 1.em
            marginLeft = Auto.auto
            marginRight = Auto.auto
            padding = Padding(0.em, 1.em)
        }

        div {
            css {
                display = Display.flex
                justifyContent = JustifyContent.flexEnd
            }

            button {
                css {
                    actionButton(NamedColor.green)
                }
                type = ButtonType.button
                onClick = {
                    enterNewUser = true
                }
                +"Add User"
            }
        }

        UsersTableView {
            entries = users ?: []
            onClick = { _, _ -> }
            config = UsersTableConfig { user ->
                coroutineScope.launch {
                    if (!confirm("Are you sure you want to remove $user")) return@launch
                    client.delete(API.Admin.User(username = user))
                    loadUsers()
                }
            }
        }
    }

    AddUserDialog {
        title = "Add User"
        open = enterNewUser
        onSubmit = {
            coroutineScope.launch {
                val response = client.post(API.Admin.User(username = it.username)) {
                    setBody(it.password)
                }
                if (!response.status.isSuccess()) alert("Couldn't add ${it.username}, the username is already taken")
                loadUsers()
            }
            enterNewUser = false
        }
        onCancel = {
            enterNewUser = false
        }
        validate = {
            it.username.isNotBlank() && it.password.isNotBlank() && it.password == it.confirm
        }
    }
}

private data class AddUserFields(
    var username: String,
    var password: String,
    var confirm: String,
)

private val AddUserDialog = Dialog(
    [
        DialogField(AddUserFields::username, "Username"),
        DialogField(AddUserFields::password, "Password", true),
        DialogField(AddUserFields::confirm, "Confirm", true),
    ]
)
